/**
 * JOIN Node Debugger
 * Diagnostic utilities untuk troubleshoot JOIN yang menghasilkan 0 records
 */

export interface JoinDiagnostic {
  leftRecords: number;
  rightRecords: number;
  leftKeyField: string;
  rightKeyField: string;
  leftKeysTotal: number;
  rightKeysTotal: number;
  leftKeysNulls: number;
  rightKeysNulls: number;
  overlappingKeys: number;
  leftOnlyKeys: number;
  rightOnlyKeys: number;
  expectedRecords: number;
  keyTypeMismatch: boolean;
  sampleLeftKeys: unknown[];
  sampleRightKeys: unknown[];
  sampleOverlappingKeys: unknown[];
  warnings: string[];
  recommendations: string[];
}

/**
 * Analyze JOIN operation untuk diagnostic purposes
 */
export function analyzeJoin(
  leftData: unknown[],
  rightData: unknown[],
  leftKey: string,
  rightKey: string
): JoinDiagnostic {
  const warnings: string[] = [];
  const recommendations: string[] = [];

  // Extract keys
  const leftKeys = leftData.map(r => (r as Record<string, unknown>)[leftKey]);
  const rightKeys = rightData.map(r => (r as Record<string, unknown>)[rightKey]);

  // Count nulls
  const leftNulls = leftKeys.filter(k => k === null || k === undefined).length;
  const rightNulls = rightKeys.filter(k => k === null || k === undefined).length;

  // Get non-null unique keys
  const leftUniqueKeys = new Set(leftKeys.filter(k => k !== null && k !== undefined));
  const rightUniqueKeys = new Set(rightKeys.filter(k => k !== null && k !== undefined));

  // Find overlapping keys
  const overlappingKeys = new Set(
    [...leftUniqueKeys].filter(k => rightUniqueKeys.has(k))
  );

  // Find keys only in left or right
  const leftOnlyKeys = new Set(
    [...leftUniqueKeys].filter(k => !rightUniqueKeys.has(k))
  );
  const rightOnlyKeys = new Set(
    [...rightUniqueKeys].filter(k => !leftUniqueKeys.has(k))
  );

  // Check data type consistency
  const leftTypes = new Set(
    leftKeys.filter(k => k !== null && k !== undefined).map(k => typeof k)
  );
  const rightTypes = new Set(
    rightKeys.filter(k => k !== null && k !== undefined).map(k => typeof k)
  );

  const keyTypeMismatch =
    leftTypes.size > 0 && rightTypes.size > 0 && ![...leftTypes].some(t => rightTypes.has(t));

  // Estimate expected records (simplified - assumes 1:1 cardinality)
  const expectedRecords = overlappingKeys.size;

  // Generate warnings
  if (leftData.length === 0) {
    warnings.push('⚠️  Left dataset is empty');
  }
  if (rightData.length === 0) {
    warnings.push('⚠️  Right dataset is empty');
  }
  if (leftNulls > 0) {
    warnings.push(`⚠️  ${leftNulls} NULL values in left key '${leftKey}' (will be skipped)`);
  }
  if (rightNulls > 0) {
    warnings.push(`⚠️  ${rightNulls} NULL values in right key '${rightKey}' (will be skipped)`);
  }
  if (overlappingKeys.size === 0) {
    warnings.push('❌ NO OVERLAPPING KEYS - Join will produce 0 records!');
  }
  if (keyTypeMismatch) {
    warnings.push(
      `❌ KEY TYPE MISMATCH - Left: ${[...leftTypes].join(',')}, Right: ${[...rightTypes].join(',')} - Join will produce 0 records!`
    );
  }
  if (leftOnlyKeys.size > 0 && leftOnlyKeys.size === leftUniqueKeys.size) {
    warnings.push('⚠️  No left keys match right keys');
  }
  if (rightOnlyKeys.size > 0 && rightOnlyKeys.size === rightUniqueKeys.size) {
    warnings.push('⚠️  No right keys match left keys');
  }

  // Generate recommendations
  if (keyTypeMismatch) {
    recommendations.push(
      `Convert keys to same type: Add TRANSFORMATION node before JOIN to convert ${leftKey} and ${rightKey} to same type (e.g., String(value))`
    );
  }
  if (overlappingKeys.size === 0 && !keyTypeMismatch) {
    recommendations.push('Check if join keys are correct - no matching values found');
    recommendations.push('Try using LEFT JOIN to see which records from left dataset exist');
  }
  if (leftNulls > 0 || rightNulls > 0) {
    recommendations.push(
      'Consider filtering out NULL values before JOIN, or use COALESCE/default values'
    );
  }
  if (expectedRecords > 0 && expectedRecords < Math.min(leftData.length, rightData.length) * 0.5) {
    recommendations.push(
      `Low match rate (${((expectedRecords / Math.min(leftData.length, rightData.length)) * 100).toFixed(1)}%) - verify join keys are correct`
    );
  }

  return {
    leftRecords: leftData.length,
    rightRecords: rightData.length,
    leftKeyField: leftKey,
    rightKeyField: rightKey,
    leftKeysTotal: leftUniqueKeys.size,
    rightKeysTotal: rightUniqueKeys.size,
    leftKeysNulls: leftNulls,
    rightKeysNulls: rightNulls,
    overlappingKeys: overlappingKeys.size,
    leftOnlyKeys: leftOnlyKeys.size,
    rightOnlyKeys: rightOnlyKeys.size,
    expectedRecords,
    keyTypeMismatch,
    sampleLeftKeys: [...leftUniqueKeys].slice(0, 5),
    sampleRightKeys: [...rightUniqueKeys].slice(0, 5),
    sampleOverlappingKeys: [...overlappingKeys].slice(0, 5),
    warnings,
    recommendations,
  };
}

/**
 * Format diagnostic report untuk console
 */
export function formatDiagnosticReport(diag: JoinDiagnostic): string {
  const lines: string[] = [];

  lines.push('═══════════════════════════════════════════════');
  lines.push('           JOIN DIAGNOSTIC REPORT');
  lines.push('═══════════════════════════════════════════════');
  lines.push('');

  lines.push('📊 DATASET SIZES:');
  lines.push(`   Left:  ${diag.leftRecords} records`);
  lines.push(`   Right: ${diag.rightRecords} records`);
  lines.push('');

  lines.push('🔑 JOIN KEYS:');
  lines.push(`   Left:  ${diag.leftKeyField}`);
  lines.push(`   Right: ${diag.rightKeyField}`);
  lines.push('');

  lines.push('🔍 KEY ANALYSIS:');
  lines.push(`   Left unique keys:  ${diag.leftKeysTotal} (${diag.leftKeysNulls} nulls)`);
  lines.push(`   Right unique keys: ${diag.rightKeysTotal} (${diag.rightKeysNulls} nulls)`);
  lines.push(`   Overlapping keys:  ${diag.overlappingKeys}`);
  lines.push(`   Left-only keys:    ${diag.leftOnlyKeys}`);
  lines.push(`   Right-only keys:   ${diag.rightOnlyKeys}`);
  lines.push('');

  lines.push('📈 EXPECTED RESULT:');
  lines.push(`   Estimated records: ${diag.expectedRecords}`);
  lines.push('');

  if (diag.sampleOverlappingKeys.length > 0) {
    lines.push('✅ SAMPLE MATCHING KEYS:');
    diag.sampleOverlappingKeys.forEach(key => {
      lines.push(`   - ${JSON.stringify(key)}`);
    });
    lines.push('');
  }

  if (diag.sampleLeftKeys.length > 0) {
    lines.push('📝 SAMPLE LEFT KEYS:');
    diag.sampleLeftKeys.forEach(key => {
      lines.push(`   - ${JSON.stringify(key)} (${typeof key})`);
    });
    lines.push('');
  }

  if (diag.sampleRightKeys.length > 0) {
    lines.push('📝 SAMPLE RIGHT KEYS:');
    diag.sampleRightKeys.forEach(key => {
      lines.push(`   - ${JSON.stringify(key)} (${typeof key})`);
    });
    lines.push('');
  }

  if (diag.warnings.length > 0) {
    lines.push('⚠️  WARNINGS:');
    diag.warnings.forEach(w => lines.push(`   ${w}`));
    lines.push('');
  }

  if (diag.recommendations.length > 0) {
    lines.push('💡 RECOMMENDATIONS:');
    diag.recommendations.forEach(r => lines.push(`   ${r}`));
    lines.push('');
  }

  lines.push('═══════════════════════════════════════════════');

  return lines.join('\n');
}

/**
 * Diagnose JOIN node execution
 * Call ini dari browser console untuk debug
 */
export async function diagnoseJoinNode(
  leftData: unknown[],
  rightData: unknown[],
  leftKey: string,
  rightKey: string
): Promise<void> {
  const diag = analyzeJoin(leftData, rightData, leftKey, rightKey);
  const report = formatDiagnosticReport(diag);

  console.log(report);

  // Return diagnostic object untuk further inspection
  (window as any).__joinDiag = diag;
  console.log('💾 Diagnostic saved to window.__joinDiag');
}
